<template>
  <div class="sidebar" :class="{ 'sidebar--collapsed': isCollapsed }">
    <!-- Logo 区域 -->
    <div class="sidebar__logo" @click="handleGoHome">
      <span class="sidebar__logo-icon">V</span>
      <span v-show="!isCollapsed" class="sidebar__logo-text">可信宝</span>
    </div>
    <!-- 导航菜单 -->
    <el-scrollbar class="sidebar__scrollbar" wrap-class="sidebar__scrollbar-wrap">
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapsed"
        :collapse-transition="false"
        background-color="#1E293B"
        text-color="#BFCBD9"
        active-text-color="#FFFFFF"
        unique-opened
        router
      >
        <template v-for="route in menuRoutes">
          <!-- 只有一个子菜单时直接显示 -->
          <el-menu-item
            v-if="route.children && route.children.length === 1 && !(route.meta && route.meta.hidden)"
            :key="route.path + '/' + route.children[0].path"
            :index="resolvePath(route.path, route.children[0].path)"
          >
            <i :class="(route.children[0].meta && route.children[0].meta.icon) || (route.meta && route.meta.icon)"></i>
            <span slot="title">{{ route.children[0].meta && route.children[0].meta.title }}</span>
          </el-menu-item>
          <!-- 多个子菜单时显示子菜单 -->
          <el-submenu
            v-else-if="route.children && route.children.length > 1 && !(route.meta && route.meta.hidden)"
            :key="route.path"
            :index="route.path"
          >
            <template slot="title">
              <i :class="route.meta && route.meta.icon"></i>
              <span>{{ route.meta && route.meta.title }}</span>
            </template>
            <el-menu-item
              v-for="child in route.children"
              :key="child.path"
              :index="resolvePath(route.path, child.path)"
            >
              <i :class="child.meta && child.meta.icon"></i>
              <span slot="title">{{ child.meta && child.meta.title }}</span>
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script>
import { constantRoutes, asyncRoutes } from '@/router/routes'

export default {
  name: 'Sidebar',
  computed: {
    /**
     * 侧边栏是否折叠
     */
    isCollapsed() {
      return this.$store.state.app.sidebarCollapsed
    },
    /**
     * 当前激活的菜单路径
     */
    activeMenu() {
      return this.$route.path
    },
    /**
     * 是否为主账号（主账号显示所有菜单）
     */
    isPrimary() {
      const userInfo = this.$store.state.user.userInfo
      return userInfo && userInfo.isPrimary
    },
    /**
     * 用户拥有的菜单权限code列表
     */
    userPermissions() {
      return this.$store.state.user.permissions || []
    },
    /**
     * 根据用户权限过滤后的菜单路由
     */
    menuRoutes() {
      const allRoutes = [...constantRoutes, ...asyncRoutes]
      const isPrimary = this.isPrimary
      const permissions = this.userPermissions

      return allRoutes.filter(route => {
        // 过滤隐藏路由和无子菜单的路由
        if (route.meta && route.meta.hidden) return false
        if (!route.children) return false

        // 主账号显示所有菜单
        if (isPrimary) return true

        // 普通账号：检查子菜单是否有权限
        const hasPermissionChild = route.children.some(child => {
          const code = child.meta && child.meta.code
          // 没有code的子菜单默认显示（如首页）
          if (!code) return true
          return permissions.includes(code)
        })

        return hasPermissionChild
      }).map(route => {
        // 主账号不过滤子菜单
        if (isPrimary) return route

        // 普通账号：过滤无权限的子菜单
        const filteredChildren = route.children.filter(child => {
          const code = child.meta && child.meta.code
          if (!code) return true
          return permissions.includes(code)
        })

        return { ...route, children: filteredChildren }
      })
    }
  },
  methods: {
    /**
     * 拼接路由路径
     * @param {string} basePath - 父级路径
     * @param {string} childPath - 子级路径
     * @returns {string}
     */
    resolvePath(basePath, childPath) {
      if (childPath.startsWith('/')) {
        return childPath
      }
      const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
      return base + '/' + childPath
    },
    /**
     * 点击 Logo 跳转首页
     */
    handleGoHome() {
      this.$router.push('/')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: $sidebar-width;
  background-color: $sidebar-bg;
  transition: width 0.3s;
  z-index: 100;
  display: flex;
  flex-direction: column;

  &--collapsed {
    width: $sidebar-collapse-width;
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: $header-height;
    cursor: pointer;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__logo-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: linear-gradient(135deg, $primary-color, #7B61FF);
    border-radius: 6px;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    font-style: italic;
  }

  &__logo-text {
    margin-left: 10px;
    font-size: 18px;
    font-weight: 600;
    color: #FFFFFF;
    letter-spacing: 2px;
  }

  &__scrollbar {
    flex: 1;
    overflow: hidden;
  }

  ::v-deep .sidebar__scrollbar-wrap {
    overflow-x: hidden;
  }

  ::v-deep .el-menu {
    border-right: none;
  }

  ::v-deep .el-menu-item {
    height: 48px;
    line-height: 48px;

    i {
      color: $sidebar-text-color;
      margin-right: 8px;
    }

    &.is-active {
      background-color: $sidebar-active-bg;
      border-right: 3px solid $primary-color;

      i {
        color: $primary-color;
      }
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }

  ::v-deep .el-submenu__title {
    height: 48px;
    line-height: 48px;

    i {
      color: $sidebar-text-color;
      margin-right: 8px;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }

  ::v-deep .el-submenu .el-menu-item {
    min-width: auto;
    padding-left: 50px !important;
  }
}
</style>
